import React from 'react';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import CheckCircle from '@panel/commons/assets/icons/check-circle.svg';
import CheckCircleVA from '@panel/commons/assets/icons/check-circle-va.svg';
import CheckCircleCM from '@panel/commons/assets/icons/check-circle-cm.svg';
import CheckCircleUB from '@panel/commons/assets/icons/check-circle-ub.svg';
import { useTranslation } from 'react-i18next';
import { getColor } from '@panel/commons/core/themev3';

const siteId = process.env.REACT_APP_BUILD_SITE;

const TipCard = ({
  device,
  tipData,
  ctaHandler,
  wordingInfo = {},
  componentDetail,
  isDisabled = false,
  hiddenButton = false,
  isPostingWithoutQuality
}) => {
  const { t } = useTranslation();
  const { tipId, tipsResponse } = tipData;
  const { isTipChecked, statusRange, differenceAverage } = tipsResponse || {};
  const isCTADisabled = isTipChecked || isDisabled || !ctaHandler;
  const isLowPriceTip = tipId === 'posting_price' && statusRange === 'BAJO' && parseInt(differenceAverage) > 50;

  const getDescription = () => {
    if (isTipChecked && isLowPriceTip) return '.low';
    if (isTipChecked) return '.ok';
    return '';
  };

  return (
    <CardContainer device={device} isPostingWithoutQuality={isPostingWithoutQuality}>
      <CardHeader
        isChecked={isTipChecked}
        device={device}
        data-panel={`performancesTips-title-${tipId}`}
        isPostingWithoutQuality={isPostingWithoutQuality}
      >
        {t(`panel.desempeno.tips.titulo.${tipId}`)}
      </CardHeader>
      <CardContent device={device}>
        <InfoContainer>
          <CardDescription data-panel={`performancesTips-description-${tipId}`}>
            {t(`panel.desempeno.tips.bajada.${tipId}${getDescription()}`, wordingInfo)}
          </CardDescription>
          {!isTipChecked && (
            <CardFocus data-panel={`performance-tipCard-${tipId}`} isPostingWithoutQuality={isPostingWithoutQuality}>
              {t(`panel.desempeno.tips.tags.${tipId}`)}
            </CardFocus>
          )}
          {componentDetail}
        </InfoContainer>
        {!hiddenButton && (
          <CardCTA
            isChecked={isTipChecked}
            onClick={() => !isCTADisabled && ctaHandler()}
            isDisabled={isDisabled}
            data-panel={`panel-tipCard-cta-${tipData.tipId}`}
          >
            {isTipChecked ? t(`panel.desempeno.tips.resultado.${tipId}`) : t(`panel.desempeno.tips.CTA.${tipId}`)}
            {isTipChecked && <div />}
          </CardCTA>
        )}
      </CardContent>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  flex: 1 1 auto;
  background-color: white;
  border: 1px solid ${select('middleGray')};
  max-width: ${({ device }) => (device === 'mobile' ? 310 : 410)}px;
  margin-right: ${({ device }) => (device === 'mobile' ? 14 : 30)}px;
  height: ${({ device, isPostingWithoutQuality }) => {
    if (device === 'mobile' && isPostingWithoutQuality) return '460px';
    if (device === 'mobile') return '477px';
    return '430px';
  }};
  border-radius: 8px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background-color: ${({ isChecked, isPostingWithoutQuality }) => {
    if (isPostingWithoutQuality) return select('negative');
    if (isChecked && siteId === 'VAMX') return select('disabledVaMx');
    return getColor('primary');
  }};
  opacity: ${({ isChecked }) => (isChecked ? '0.5' : '1')};
  height: 54px;
  padding-left: ${({ device }) => (device === 'mobile' ? 20 : 30)}px;
  color: white;
  font-family: 'Hind';
  font-style: normal;
  font-weight: 600;
  font-size: ${({ device }) => (device === 'mobile' ? 18 : 20)}px;
  display: flex;
  align-items: center;
`;

const CardDescription = styled.div`
  font-family: 'Hind';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  white-space: break-spaces;
  color: ${select('darkGray')};
`;

const InfoContainer = styled.div``;

const CardContent = styled.div`
  padding: ${({ device }) => (device === 'mobile' ? 20 : 30)}px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 88%;
`;

const CardFocus = styled.div`
  white-space: break-spaces;
  border-radius: 8px;
  border: 1px solid ${select('middleGray')};
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 20px;
  color: ${({ isPostingWithoutQuality }) => (!isPostingWithoutQuality ? getColor('primary') : select('negative'))};
  font-family: 'Hind';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
`;

const CardCTA = styled.button`
  font-family: 'Hind';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid ${getColor('primary')};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  cursor: pointer;
  :hover {
    background-color: ${getColor('tertiary')};
  }
  > div {
    background-image: ${() => {
      if (siteId === 'VAMX') return `url(${CheckCircleVA})`;
      if (siteId === 'CMBR') return `url(${CheckCircleCM})`;
      if (siteId === 'UBPE') return `url(${CheckCircleUB})`;
      return `url(${CheckCircle})`;
    }};
    width: 24px;
    height: 24px;
    background-size: cover;
    background-repeat: no-repeat;
    margin-left: 12px;
  }
  ${({ isChecked }) =>
    isChecked &&
    css`
      border: none;
      color: ${getColor('primary')};
      font-weight: 600;
      font-size: 20px;
      cursor: auto;
      :hover {
        background-color: unset;
      }
    `}

  ${({ isDisabled, isChecked }) =>
    isDisabled &&
    !isChecked &&
    css`
      background-color: ${select('lightGray')};
      border: none;
      color: ${select('midLightGray')}
      cursor: auto;
      :hover{
        background-color: ${select('lightGray')};
      }
    `}
`;

export default TipCard;
